/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { InfoIcon, MapPinIcon, NavigationIcon, RadarIcon } from 'lucide-react'
import { type ComponentProps, forwardRef, type ReactNode } from 'react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { StaticMinimap } from '@/mapbox/static-minimap'

const KM_TO_MILES = 0.621_371

export interface LocationCardProps extends Omit<ComponentProps<typeof Card>, 'children' | 'title'> {
  distanceKm: number
  distanceTitle?: ReactNode
  hideRangeTitle?: boolean
  latitude: number
  /** Human-readable location label (e.g. city or "Near …"). */
  location: ReactNode
  longitude: number
  mapboxToken: string
  mapHeight?: number
  mapWidth?: number
  maxW?: string
  rangeKm?: number
  rangeTitle?: ReactNode
  isPrivate?: boolean
  showSupporterPlaceholder?: boolean
  supporterPlaceholderSrc?: string
  supporterPlaceholderAlt?: string
  title?: ReactNode
  /** Default title used when `isPrivate` is `false` and no `title` prop is provided. */
  defaultTitle?: ReactNode
  /** Default title used when `isPrivate` is `true` and no `title` prop is provided. */
  privateTitle?: ReactNode
  /** Text shown inside the private-location alert. */
  privateAlertMessage?: ReactNode
  zoom?: number
}

const DEFAULT_TITLE = 'Location:'
const DEFAULT_PRIVATE_TITLE = 'Private Location:'
const DEFAULT_DISTANCE_TITLE = 'Distance:'
const DEFAULT_RANGE_TITLE = 'Range:'
const DEFAULT_PRIVATE_ALERT_MESSAGE = 'This is a private location. Please do not disclose this location to anyone.'
const DEFAULT_SUPPORTER_PLACEHOLDER_SRC = '/map-supporter.webp'
const DEFAULT_SUPPORTER_PLACEHOLDER_ALT = 'Map available to supporters only'

export const LocationCard = forwardRef<HTMLDivElement, LocationCardProps>((props, forwardedRef) => {
  const {
    title,
    defaultTitle = DEFAULT_TITLE,
    privateTitle = DEFAULT_PRIVATE_TITLE,
    distanceTitle = DEFAULT_DISTANCE_TITLE,
    rangeTitle = DEFAULT_RANGE_TITLE,
    privateAlertMessage = DEFAULT_PRIVATE_ALERT_MESSAGE,
    distanceKm,
    location,
    latitude,
    longitude,
    isPrivate = false,
    rangeKm,
    mapboxToken,
    hideRangeTitle = true,
    showSupporterPlaceholder = false,
    supporterPlaceholderSrc = DEFAULT_SUPPORTER_PLACEHOLDER_SRC,
    supporterPlaceholderAlt = DEFAULT_SUPPORTER_PLACEHOLDER_ALT,
    zoom,
    mapWidth,
    mapHeight,
    maxW,
    className,
    ...rest
  } = props

  const resolvedTitle = title ?? (isPrivate ? privateTitle : defaultTitle)
  const resolvedMapHeightPx = mapHeight ?? 310
  const resolvedMapWidthPx = mapWidth ?? 310

  const distanceMiles = (distanceKm ?? 0) * KM_TO_MILES
  const rangeMiles = (rangeKm ?? 0) * KM_TO_MILES
  const showRangeRow = rangeKm != null && !hideRangeTitle

  return (
    <Card className={cn('w-full min-w-0', className)} ref={forwardedRef} {...rest}>
      <CardContent>
        <div className="flex w-full flex-col items-start gap-3 self-start pb-2">
          <div className="flex w-full flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              <MapPinIcon className="size-5 text-muted-foreground" />
              <span className="text-muted-foreground">{resolvedTitle}</span>
            </div>
            <div className="text-foreground">{location}</div>
            {isPrivate ? (
              <Alert variant="destructive">
                <InfoIcon className="text-destructive" />
                <AlertDescription>{privateAlertMessage}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <div className="flex w-full items-start gap-2">
            <NavigationIcon className="mt-0.5 size-5 text-muted-foreground" />
            <span className="text-muted-foreground">{distanceTitle}</span>
            <span className="text-foreground">
              {distanceKm} km / {distanceMiles.toFixed(1)} mi
            </span>
          </div>

          {showRangeRow ? (
            <div className="flex w-full items-start gap-2">
              <RadarIcon className="mt-0.5 size-5 text-muted-foreground" />
              <span className="text-muted-foreground">{rangeTitle}</span>
              <span className="text-foreground">
                {rangeKm} km / {rangeMiles.toFixed(1)} mi
              </span>
            </div>
          ) : null}

          <div className="flex w-full flex-col items-stretch">
            {showSupporterPlaceholder ? (
              <div
                className="mx-auto w-full min-w-0 max-w-full overflow-hidden rounded-md border border-border bg-black"
                style={{
                  height: `${resolvedMapHeightPx}px`,
                  maxWidth: maxW ?? '100%',
                }}
              >
                <img
                  alt={supporterPlaceholderAlt}
                  className="h-full w-full min-w-0 max-w-full object-contain"
                  height={resolvedMapHeightPx}
                  src={supporterPlaceholderSrc}
                  width={resolvedMapWidthPx}
                />
              </div>
            ) : (
              <StaticMinimap
                lat={latitude}
                lon={longitude}
                mapboxToken={mapboxToken}
                mapHeight={resolvedMapHeightPx}
                mapWidth={resolvedMapWidthPx}
                maxW={maxW}
                rangeKm={rangeKm}
                zoom={zoom}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
})

LocationCard.displayName = 'LocationCard'
